import { useExperiment, useExperimentVariant } from "@hopper-b2b/experiments";
import { useAuthProvider } from "@hopper-b2b/utilities";
import { RootWithAuth as NavigationWithAuth } from "@hopper-b2b/hopper-navigation";

import { HopperThemingProvider } from "@hopper-b2b/hopper-theming";
import {
  PATH_HOMES,
  PATH_HOTELS,
  PATH_HOTELS_BOOK,
  PATH_HOTELS_NEW_CONFIRMATION,
  PATH_HOTELS_ROOT,
  PATH_STAYS,
} from "@hopper-b2b/hopper-utils";
import { useI18nContext } from "@hopper-b2b/i18n";
import { AtHotelFooter, Footer } from "@hopper-b2b/ui-core";
import {
  FeatureFlagsContext,
  FeatureFlagsContextProps,
  getEnvVariables,
  isAtHotelBrand,
  isHopperAppWebView,
  useDeviceTypes,
} from "@hopper-b2b/utilities";
import { Box } from "@mui/material";
import clsx from "clsx";
import { useEffect, useMemo } from "react";
import {
  Route,
  Routes,
  matchPath,
  useLocation,
} from "react-router-dom-v5-compat";
import styles from "./App.module.scss";
import { FEATURE_FLAGS } from "./Root";
import { Body } from "./components/Body";
import {
  SupportChatbotProvider,
  useSupportChatbotFactory,
} from "@hopper-b2b/chat";
import { SupportModal } from "./components/SupportModal";

const googleIdentityClientId = getEnvVariables(
  "googleIdentityClientId"
)?.toString();

const PX_CLOUD_SRC = import.meta.env.VITE_PX_CLOUD_SRC;

export const App = () => {
  const { language } = useI18nContext();
  const { state } = useAuthProvider();
  const { matchesMobile } = useDeviceTypes();

  // Sending data to sift with each pathname/search load
  const location = useLocation();

  const supportChatVariant = useExperimentVariant(
    FEATURE_FLAGS.CX_CHATBOT_TYPE
  );
  const chatbot = useSupportChatbotFactory(supportChatVariant);
  const enableUserCentricsBanner = useExperiment(
    FEATURE_FLAGS.USER_CENTRICS_BANNER
  );

  useEffect(() => {
    if (enableUserCentricsBanner) {
      const USER_CENTRICS_ELEMENT_ID = "usercentrics-cmp";
      if (!document.getElementById(USER_CENTRICS_ELEMENT_ID)) {
        const userCentricsScript = document.createElement("script");
        userCentricsScript.id = USER_CENTRICS_ELEMENT_ID;
        userCentricsScript.async = true;
        userCentricsScript.src = "https://web.cmp.usercentrics.eu/ui/loader.js";
        userCentricsScript.setAttribute("data-settings-id", "Qh_ZUhk0sRIR18");
        document.head.appendChild(userCentricsScript);
      }
    }
  }, [enableUserCentricsBanner]);

  useEffect(() => {
    // Do not send Sift events for delegated concierge actions
    if (!state?.sessionInfo?.delegatedTo) {
      const _sift = (window._sift = window._sift || []);
      _sift.push(["_trackPageview"]);
    }
  }, [location.pathname, location.search, state]);

  useEffect(() => {
    if (location.pathname.includes("/book")) {
      if (!document.getElementById("px-cloud")) {
        const head = document.getElementsByTagName("head")[0];
        const script = document.createElement("script");
        script.src = PX_CLOUD_SRC;
        script.id = "px-cloud";
        script.async = true;
        head.appendChild(script);
      }
    }
  }, [location.pathname]);
  // TODO Enable Darkmode for Hopper Web
  // const enableDarkMode = useExperiment(FEATURE_FLAGS.APP_DARKMODE);

  const enableFrenchTranslations = useExperiment(FEATURE_FLAGS.FR_TRANSLATIONS);

  // Auth Flags
  const enablePhoneAuth = useExperiment(FEATURE_FLAGS.PHONE_AUTH);
  const enableGoogleSsoAuth = useExperiment(FEATURE_FLAGS.GOOGLE_SSO_AUTH);

  const enablePriceWatch = useExperiment(FEATURE_FLAGS.AIR_PRICE_WATCH);
  const enablePriceFreeze = useExperiment(FEATURE_FLAGS.AIR_PRICE_FREEZE);
  const enablePricePrediction = useExperiment(
    FEATURE_FLAGS.AIR_PRICE_PREDICTION
  );

  const enableScheduleChange = useExperiment(FEATURE_FLAGS.AIR_DISRUPTION);
  const enableMissedConnection = useExperiment(
    FEATURE_FLAGS.AIR_MISSED_CONNECTION
  );
  const enableCfar = useExperiment(FEATURE_FLAGS.AIR_CFAR);
  const enableChfar = useExperiment(FEATURE_FLAGS.AIR_CHFAR);
  const enableVipSupport = useExperiment(FEATURE_FLAGS.VIP_SUPPORT);
  const enableDarkMode = useExperiment(FEATURE_FLAGS.APP_DARKMODE);
  const enableAirWallet = useExperiment(FEATURE_FLAGS.AIR_WALLET);
  const enableLodgingWallet = useExperiment(FEATURE_FLAGS.LODGING_WALLET);
  const enableLodgingPromotionsMerchandising = useExperiment(
    FEATURE_FLAGS.LODGING_PROMOTIONS_MERCHANDISING
  );
  const enableDisruptionProtection = useExperiment(
    FEATURE_FLAGS.AIR_DISRUPTION
  );
  const enableDisruptionDelayThresholdChangeHighlight = useExperiment(
    FEATURE_FLAGS.DISRUPTION_DELAY_THRESHOLD_CHANGE_HIGHLIGHT
  );
  const enableAirShopV4 = true; //Default should use v4

  const enableAirExchange = useExperiment(FEATURE_FLAGS.AIR_EXCHANGE);
  const enableHFv2 = useExperiment(FEATURE_FLAGS.HFv2);

  const enableStays = useExperiment(FEATURE_FLAGS.STAYS);
  const enableHomesInStaysSearch = useExperiment(
    FEATURE_FLAGS.HOMES_IN_STAYS_SEARCH
  );

  const enableHomesMyTrips = useExperiment(FEATURE_FLAGS.HOMES_IN_MY_TRIPS);

  const enableLodgingTripInsurance = useExperiment(
    FEATURE_FLAGS.LODGING_TRIP_INSURANCE
  );

  const hideInCheckout = matchPath(PATH_HOTELS_BOOK, location.pathname);
  const hideInConfirmation = matchPath(
    PATH_HOTELS_NEW_CONFIRMATION,
    location.pathname
  );

  const enableScrantonWebImprovedMerchandising = useExperiment(
    FEATURE_FLAGS.SCRANTON_WEB_IMPROVED_MERCHANDISING
  );

  const enableHotelCugDeals = useExperiment(FEATURE_FLAGS.HOTEL_CUG_DEALS);
  const enableProgressiveLodgingList = useExperiment(
    FEATURE_FLAGS.PROGRESSIVE_LODGING_LIST
  );

  const featureFlags: FeatureFlagsContextProps = useMemo(
    () => ({
      enableAirExchange,
      enableCfar,
      enableChfar,
      enableDarkMode,
      enableHFv2,
      enableMissedConnection,
      enablePriceFreeze,
      enablePriceWatch,
      enablePricePrediction,
      enableScheduleChange,
      enableVipSupport,
      enableWallet: enableAirWallet || enableLodgingWallet,
      enableDisruptionProtection,
      enableAirShopV4,
      enableStays,
      enableHomesInStaysSearch,
      enableLodgingPromotionsMerchandising,
      enableHomesMyTrips,
      enableLodgingTripInsurance,
      enableScrantonWebImprovedMerchandising,
      enableHotelCugDeals,
      enableProgressiveLodgingList,
      enableDisruptionDelayThresholdChangeHighlight,
      supportChatVariant,
    }),
    [
      enableLodgingTripInsurance,
      enableAirExchange,
      enableCfar,
      enableChfar,
      enableDarkMode,
      enableHFv2,
      enableMissedConnection,
      enablePriceFreeze,
      enablePriceWatch,
      enablePricePrediction,
      enableScheduleChange,
      enableVipSupport,
      enableAirWallet,
      enableDisruptionProtection,
      enableAirShopV4,
      enableStays,
      enableHomesInStaysSearch,
      enableLodgingPromotionsMerchandising,
      enableLodgingWallet,
      enableHomesMyTrips,
      enableScrantonWebImprovedMerchandising,
      enableHotelCugDeals,
      enableProgressiveLodgingList,
      enableDisruptionDelayThresholdChangeHighlight,
      supportChatVariant,
    ]
  );

  return (
    <Box className={clsx(styles.container, "app-container")}>
      <SupportChatbotProvider chatbot={chatbot}>
        <FeatureFlagsContext.Provider value={featureFlags}>
          {/* THE USE OF REACT ROUTER HERE IS TEMPORARY UNTIL WE ROLL OUT THE NEW NAVIGATION TO ALL ROUTES */}
          <Routes>
            <Route path={PATH_HOTELS} element={<></>} />
            <Route path={PATH_STAYS} element={<></>} />
            <Route
              path={PATH_HOMES}
              element={
                // Hide header in webview
                !isHopperAppWebView() && (
                  <NavigationWithAuth
                    showLanguageSelect={enableFrenchTranslations}
                    onLanguageSelect={() => {
                      window.location.reload();
                    }}
                    googleIdentityClientId={googleIdentityClientId}
                    isPhoneAuthEnabled={enablePhoneAuth}
                    isGoogleSsoAuthEnabled={enableGoogleSsoAuth}
                  />
                )
              }
            />
            <Route
              path="/*"
              element={
                <NavigationWithAuth
                  showLanguageSelect={enableFrenchTranslations}
                  onLanguageSelect={() => {
                    window.location.reload();
                  }}
                  googleIdentityClientId={googleIdentityClientId}
                  isPhoneAuthEnabled={enablePhoneAuth}
                  isGoogleSsoAuthEnabled={enableGoogleSsoAuth}
                />
              }
            />
            <Route
              path={PATH_HOTELS_ROOT}
              element={
                <NavigationWithAuth
                  showLanguageSelect={enableFrenchTranslations}
                  onLanguageSelect={() => {
                    window.location.reload();
                  }}
                  googleIdentityClientId={googleIdentityClientId}
                  isPhoneAuthEnabled={enablePhoneAuth}
                  isGoogleSsoAuthEnabled={enableGoogleSsoAuth}
                />
              }
            />
          </Routes>
        </FeatureFlagsContext.Provider>
        <Routes>
          <Route path="/*" element={<Body />} />
        </Routes>
        {!hideInCheckout && !hideInConfirmation && !matchesMobile && (
          <HopperThemingProvider enableHDSTheme>
            {isAtHotelBrand() ? (
              <AtHotelFooter />
            ) : (
              <Footer language={language} />
            )}
          </HopperThemingProvider>
        )}
        <SupportModal />
      </SupportChatbotProvider>
    </Box>
  );
};
